import React from 'react'
import { Theme, withStyles } from '@material-ui/core/styles'
import Dialog from '@material-ui/core/Dialog'
import MuiDialogContent from '@material-ui/core/DialogContent'
import MuiDialogActions from '@material-ui/core/DialogActions'
import { Grid, makeStyles } from '@material-ui/core'
import { isMobile } from 'react-device-detect'
import close from '../assets/images/colosePopup.svg'

const DialogContent = withStyles((theme: Theme) => ({
  root: {
    padding: 0,
  },
}))(MuiDialogContent)

const DialogActions = withStyles((theme: Theme) => ({
  root: {
    margin: 0,
    padding: theme.spacing(1),
  },
}))(MuiDialogActions)

interface ChartTooltipModalProps {
  open?: any
  setOpen?: any
  handleClose?: any
}
const useStyles = makeStyles({
  content: {
    background: '#FFFFFF',
    boxSizing: 'border-box',
    borderRadius: 3,
    maxWidth: 820,
    width: '100%',
    maxHeight: '80vh',
    display: 'flex',
    flexDirection: 'column',
    padding: '4px 24px 24px 24px',
  },
  title: {
    color: '#181818',
    fontFamily: 'Inter, sans-serif',
    fontSize: 24,
    lineHeight: '28px',
    fontWeight: 600,
  },
  subTitle: {
    color: '#5E5E5E',
    fontFamily: 'Inter, sans-serif',
    fontSize: 24,
    lineHeight: '28px',
    fontWeight: 400,
  },
  detailContent: {
    marginBottom: 12,
  },
  header: {
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'space-between',
    paddingBottom: 12,
    marginBottom: 16,
    borderBottom: '1px solid #DFE4EB',
    '& input': {
      width: 36,
      height: 36,
      borderRadius: 999,
      background: '#F2F2F2',
      boxSizing: 'border-box',
    },
  },
  body: {
    overflowY: 'auto',
    paddingRight: 4,
  },
  typeHead: {
    fontFamily: 'Inter, sans-serif',
    fontSize: 14,
    lineHeight: '22px',
    fontWeight: 700,
    color: '#1B3963',
  },
  typeDetail: {
    fontFamily: 'Inter, sans-serif',
    fontWeight: 500,
    fontSize: 14,
    lineHeight: '22px',
    color: '#475569',
    '& b': {
      fontWeight: 700,
      color: '#1B3963',
    },
  },
})

const FundamentalTooltipModal = ({
  open,
  setOpen,
  handleClose,
}: ChartTooltipModalProps) => {
  const classes = useStyles()
  return (
    <div>
      <Dialog
        onClose={handleClose}
        aria-labelledby="customized-dialog-title"
        open={open}
        maxWidth={false}
        PaperProps={{ style: { maxWidth: 820, width: '100%' } }}
      >
        <DialogContent>
          <div className={classes.content}>
            {!isMobile && (
              <div className={classes.header}>
                <div>
                  <span className={classes.title}>Fundamental Analysis </span>
                  <span className={classes.subTitle}>
                    (Street Perception & Investor Confidence Measures)
                  </span>
                </div>
                <input
                  type="image"
                  onClick={() => handleClose()}
                  src={close}
                  alt="closeBtn"
                />
              </div>
            )}

            {isMobile && (
              <>
                <div className={classes.header} style={{ marginBottom: 5 }}>
                  <div>
                    <span className={classes.title}>Fundamental Analysis </span>
                    <span className={classes.subTitle}>
                      (Street Perception & Investor Confidence Measures)
                    </span>
                  </div>
                  <input
                    type="image"
                    onClick={() => handleClose()}
                    src={close}
                    alt="closeBtn"
                  />
                </div>
              </>
            )}
            <div className={classes.body}>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>Fundamental Analysis: </span>
                <span className={classes.typeDetail}>
                  The grid below is designed to provide a quick and efficient
                  ‘scan’ of the major financial elements of a company –
                  specifically: funding and capital structure; assets; outputs
                  (e.g. sales, profits, cashflows) and market valuation. Viewed
                  left to right in columns, it shows the normal flow of a
                  company which is using funding to build assets, and then
                  generating outputs from those assets. The quality and
                  consistency of these outputs then impact the cost and
                  availability of funding through the mechanism of market
                  feedback. All types of companies are not the same, you will
                  see different “scans” for different sectors, Financials
                  (Banks) and Property will be a different set of compositions
                  and ratios to non-financial companies.
                </span>
              </div>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>Composition Charts: </span>
                <span className={classes.typeDetail}>
                  Each column contains a composition chart at the top, which
                  shows the composition of that element of the company over
                  time. This can be viewed either in absolute values, or in %
                  composition, and allows you to quickly see abnormalities,
                  dislocations and changes.
                </span>
              </div>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>Line Charts:</span>
                <span className={classes.typeDetail}>
                  Underneath each composition chart are two indicators which are
                  proxy ratios for broad financial health in that part of the
                  company (e.g. asset liquidity and asset stability). These line
                  charts express the company’s latest values in standard
                  deviations i.e. ‘how far away from normal are they?’ – both
                  against the company history for that company, and against a
                  country/sector peer group where available. To understand what
                  ‘normal’ looks like requires a certain amount of datapoints,
                  younger companies are benchmarked against their peer group
                  only. Each chart can be maximised to a bigger view, and also
                  contains a specific ‘I’ text explaining what ratios are being
                  used, why, and how to interpret them.
                </span>
              </div>
              <div className={classes.detailContent}>
                <span className={classes.typeHead}>Using the Scan:</span>
                <span className={classes.typeDetail}>
                  To use this grid, simply scan it and look for abnormalities in
                  the composition charts and/or extreme highs or lows in the
                  line charts. Read the ‘I’ text on any abnormal charts for tips
                  on how to interpret it, and on what to do next. Also bear in
                  mind that this is a high-level overview. There may be
                  specific, granular ratios we are looking at – for example in
                  our forensic accounting analysis. Where there is a specific
                  financial health issue, such as asset disposal losses or
                  accruals, we will state this in your risk story feed. This has
                  been built to be dynamic, only charts which are believed to
                  offer useful insights are displayed, the composition and line
                  charts view will adapt to sector, information value and data
                  quality. On the line charts, if the company does not have
                  enough fundamentals history to determine it’s “norm” we will
                  display only the peer group analysis.
                </span>
              </div>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  )
}

export default React.memo(FundamentalTooltipModal)
